/**
 * Every configurable value in the app, read once from import.meta.env.
 *
 * Names match scripts/seed-env.js, which is what writes these into SSM and
 * materializes .env; see .env.example. Nothing else in the app should touch
 * import.meta.env directly.
 */

function trimTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

const rawApiUrl = import.meta.env.VITE_API_URL ?? '';

export const config = {
  /** Origin of slate-api. Calls are made to `${apiUrl}/v1/...`. */
  apiUrl: trimTrailingSlash(rawApiUrl || 'http://localhost:3000'),

  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_CONFIG_APIKEY ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_CONFIG_AUTHDOMAIN ?? '',
    projectId: import.meta.env.VITE_FIREBASE_CONFIG_PROJECTID ?? '',
    storageBucket: import.meta.env.VITE_FIREBASE_CONFIG_STORAGEBUCKET ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_CONFIG_MESSAGINGSENDERID ?? '',
    appId: import.meta.env.VITE_FIREBASE_CONFIG_APPID ?? '',
  },

  /**
   * GA4, the property attached to the Firebase project. The marketing site
   * reports to the same one, so a visit that starts on an ad and ends in a
   * signup here is one journey. Empty in local builds: nothing loads.
   */
  gaMeasurementId: import.meta.env.VITE_FIREBASE_CONFIG_MEASUREMENTID ?? '',

  /** ~2.5 Mbps, tuned for 1080p screen content. */
  recorderBitrate: Number(import.meta.env.VITE_RECORDER_BITRATE) || 2_500_000,
} as const;

/** Is Firebase configured well enough to attempt auth? */
export const firebaseConfigured = Boolean(config.firebase.apiKey && config.firebase.projectId);

/** Origin used when building links we hand to other humans. */
export function publicOrigin(): string {
  const override = import.meta.env.VITE_PUBLIC_ORIGIN;
  if (override) return trimTrailingSlash(override);
  return window.location.origin;
}

export function shareUrl(shareId: string): string {
  return `${publicOrigin()}/v/${shareId}`;
}

export function requestLinkUrl(token: string): string {
  return `${publicOrigin()}/r/${token}`;
}
